// Nurse1ActivePatientCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../layout/AppLayout";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

type WaitingItem = {
  _id: string;
  createdAt: string;
  startedAt?: string;
  sessionStartAt?: string;
  stationId: string;
  sessionId?: string;
  session_id?: string;
  session?: { id?: string; sessionId?: string };
  child: {
    name: string;
    age: number;
    gender: string;
    emrId: string;
    childId: string;
    profile?: string;
  };
};

type StationPatientsResp = {
  waiting?: WaitingItem[];
  inProgress?: WaitingItem[];
  completed?: WaitingItem[];
};

interface ActivePatientCardProps {
  onPatientSelect?: (patient: {
    id: string;
    name: string;
    age: number;
    gender: string;
    emrId?: string;
  }) => void;
  showActions?: boolean;
  sessionId?: string;
  stationId?: string;
  validateBeforeComplete?: () => boolean;

  formStatus?: {
    anthropometry: boolean;
    physical: boolean;
    adMinor: boolean;
    buSample: boolean;
    physicalSymptomsObserved: boolean;
  };

  showADForm?: boolean;
  onStationStarted?: () => void;
}

function readSessionIdFromItem(it?: WaitingItem): string | undefined {
  if (!it) return undefined;
  return (
    it.sessionId ||
    (it as any).session_id ||
    it.session?.id ||
    it.session?.sessionId ||
    undefined
  );
}

function readSessionIdFromStorage(): string | undefined {
  return (
    sessionStorage.getItem("workflowSessionId") ||
    sessionStorage.getItem("lastSessionId") ||
    sessionStorage.getItem("currentSessionId") ||
    undefined
  );
}

function clearStoredSessionIds() {
  ["workflowSessionId", "lastSessionId", "currentSessionId"].forEach((k) =>
    sessionStorage.removeItem(k),
  );
}

type Queues = {
  waiting: WaitingItem[];
  inProgress: WaitingItem[];
  completed: WaitingItem[];
};

const Nurse1ActivePatientCard: React.FC<ActivePatientCardProps> = ({
  onPatientSelect,
  showActions = false,
  sessionId,
  stationId = "NURSE1",
  validateBeforeComplete,
  formStatus, // ✅ ADD
  showADForm, // ✅ ADD
  onStationStarted, // ✅ ADD
}) => {
  const outlet = useOutletContext<any>();

  const activePatient = outlet?.activePatient ?? null;
  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  const [patients, setPatients] = useState<Queues>({
    waiting: [],
    inProgress: [],
    completed: [],
  });
  const [effectiveSessionId, setEffectiveSessionId] = useState<string>();

  const loadQueue = async () => {
    try {
      // ✅ Get centerId
      const centerId =
        sessionStorage.getItem("centerId") || localStorage.getItem("centerId");

      if (!centerId) {
        console.warn("Center ID not found");
        setPatients({ waiting: [], inProgress: [], completed: [] });
        return;
      }

      // ✅ Get date (same format used earlier)
      const date = new Date().toISOString().slice(0, 10);

      // ✅ NEW API CALL
      const { data } = await api.centerworkflowApi.get(
        `/queues/NURSE1_Q?center_id=${encodeURIComponent(
          centerId,
        )}&date=${encodeURIComponent(date)}`,
      );

      setPatients({
        waiting: data?.waiting ?? [],
        inProgress: data?.inProgress ?? [],
        completed: data?.completed ?? [],
      });
    } catch (e) {
      setPatients({ waiting: [], inProgress: [], completed: [] });
    }
  };

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  const current = useMemo(() => {
    // Priority 1 → current patient from AppLayout
    if (activePatient?._id) {
      return activePatient;
    }

    // Priority 2 → queue in progress
    if (patients?.inProgress?.length) {
      return patients.inProgress[0];
    }

    return null;
  }, [activePatient, patients]);

  useEffect(() => {
    const fromItem = readSessionIdFromItem(current);
    const fromStore = readSessionIdFromStorage();
    const resolved = sessionId || fromItem || fromStore;
    setEffectiveSessionId(resolved);
  }, [sessionId, current]);

  /* ================= IMAGE FETCH LOGIC (FIXED) ================= */
  // useEffect(() => {
  //   const loadAvatar = async () => {
  //     // 1. Reset logic
  //     setAvatarUrl(undefined);

  //     if (!current?.child?.childId) return;

  //     try {
  //       const token = sessionStorage.getItem("accessToken");
  //       if (!token) return;
  //       const headers = { Authorization: `Bearer ${token}` };

  //       const centerId =
  //         sessionStorage.getItem("centerId") ||
  //         localStorage.getItem("centerId");

  //       if (!centerId) return;

  //       const effectiveDate = new Date().toISOString().slice(0, 10);

  //       const apptRes = await api.appointment_api.get(
  //         `/api/appointments/center/${encodeURIComponent(
  //           centerId,
  //         )}/date?date=${encodeURIComponent(effectiveDate)}`,
  //         { headers },
  //       );

  //       const bookings = apptRes.data?.bookings || [];

  //       // Robust match
  //       const targetId = current.child.childId?.trim();
  //       const match = bookings.find(
  //         (b: any) => b.child_id && b.child_id.trim() === targetId,
  //       );

  //       const filePath = match?.child_profile;
  //       if (!filePath) return;

  //       // Optimization: if signed, set immediately
  //       if (filePath.includes("X-Amz-Signature")) {
  //         setAvatarUrl(filePath);
  //         return;
  //       }

  //       // ✅ FIX: Manual string splitting instead of new URL()
  //       const parts = filePath.split("/");

  //       // Safety check
  //       if (parts.length < 3) {
  //         console.warn("Invalid file path format:", filePath);
  //         return;
  //       }

  //       // Extract Module
  //       const module = `${parts[0]}/${parts[1]}`;

  //       // Extract Key
  //       const key = parts.slice(2).join("/");

  //       const img = await api.S3config.get("/view-url", {
  //         params: { module, key },
  //         headers,
  //       });

  //       const signedUrl = img.data?.url || (img as any).url;

  //       if (signedUrl) {
  //         setAvatarUrl(signedUrl);
  //       }
  //     } catch (err) {
  //       console.error("Nurse1 avatar load failed", err);
  //     }
  //   };

  //   loadAvatar();
  // }, [current?.child?.childId]);
  /* ================================================= */

  /* ================= IMAGE FETCH LOGIC ================= */
  useEffect(() => {
    const loadAvatar = async () => {
      setAvatarUrl(undefined);

      const profileKey = current?.child?.profile;

      if (!profileKey) return;

      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) return;

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // If backend already gives a presigned URL, use it directly.
        if (profileKey.includes("X-Amz-Signature")) {
          setAvatarUrl(profileKey);
          return;
        }

        const parts = profileKey.split("/");

        if (parts.length < 3) {
          console.warn("Invalid profile key:", profileKey);
          return;
        }

        // Example:
        // checkin/capture-photo/P-2026-0000113-0/filename.jpg
        //
        // module = checkin/capture-photo
        // key    = P-2026-0000113-0/filename.jpg

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        const response = await api.S3config.get("/view-url", {
          params: {
            module,
            key,
          },
          headers,
        });

        const signedUrl = response.data?.url || (response as any).url;

        if (signedUrl) {
          setAvatarUrl(signedUrl);
        }
      } catch (err) {
        console.error("Nurse1 avatar load failed:", err);
      }
    };

    loadAvatar();
  }, [current?.child?.profile]);
  /* ================================================= */

  /* ================================================= */

  useEffect(() => {
    if (!current) return;
    onPatientSelect?.({
      id: current._id,
      name: current.child?.name ?? "",
      age: Number(current.child?.age ?? 0),
      gender: current.child?.gender
        ? String(current.child.gender).toUpperCase().startsWith("F")
          ? "Female"
          : "Male"
        : "-",
      emrId: current.child?.childId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  const displayAge = current?.child?.age ? `${current.child.age} Years` : "-";
  const displayGender = current?.child?.gender
    ? String(current.child.gender).toUpperCase().startsWith("F")
      ? "Female"
      : "Male"
    : "-";

  const doPost = async (path: "start" | "complete" | "cancel") => {
    const sessionId =
      effectiveSessionId ||
      current?.sessionId ||
      sessionStorage.getItem("sessionId");

    if (!sessionId || !stationId) {
      alert(
        "No active session id found. Pick a patient or start a new session.",
      );
      return;
    }

    try {
      setLoading(true);

      let body: any = {};

      if (path === "complete") {
        const completedForms: string[] = [];

        if (formStatus?.anthropometry) completedForms.push("ANTHROPOMETRY");
        if (formStatus?.physical) completedForms.push("DERMA");
        if (showADForm && formStatus?.adMinor) completedForms.push("AD");
        if (formStatus?.buSample) completedForms.push("B_AND_U_SAMPLE");

        body = {
          completedForms,
          payload: {
            AD: {
              showADForm: !!showADForm,
            },
            physicalExam: {
              symptomsObserved: formStatus?.physicalSymptomsObserved
                ? "YES"
                : "NO",
            },
          },
        };
      }

      /* ✅ UPDATE FORM FLAGS DURING COMPLETE */
      if (path === "complete") {
        await api.centerworkflowApi.put(
          `/sessions/${encodeURIComponent(sessionId)}/form-flags`,
          {
            showADForm: !!showADForm,
            showPhysicalExaminationForm: !!formStatus?.physicalSymptomsObserved,
          },
        );
      }

      const res = await api.centerworkflowApi.post(
        `/sessions/${encodeURIComponent(sessionId)}/stations/${encodeURIComponent(stationId)}/${path}`,
        body, // ✅ NOW SENDING DATA
      );

      if (path === "start") {
        alert(`Station "${stationId}" started successfully`);

        onStationStarted?.();

        // ✅ force queue refresh immediately
        await loadQueue();

        // ✅ notify all listeners
        window.dispatchEvent(new Event("patientUpdated"));
      } else if (path === "cancel") {
        alert(`Station "${stationId}" cancelled successfully`);
      } else {
        if (res.data?.isComplete) {
          clearStoredSessionIds();
          alert("🎉 Workflow completed for this session");
        } else if (res.data?.nextStation) {
          alert(`Next station: ${res.data.nextStation}`);
        } else {
          alert("Station completed");
        }
        // ✅ Notify Operations Board to refresh
        window.dispatchEvent(new Event("patientUpdated"));
      }

      if (path !== "start") {
        await loadQueue();

        outlet?.setActivePatient?.(null);

        sessionStorage.removeItem("selectedPatient_NURSE1");
        sessionStorage.removeItem("selectedPatient_NURSE1_Q");

        window.dispatchEvent(new Event("patientUpdated"));
      }
    } catch (err) {
      alert(`Failed to ${path} station`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="patient-details-card" style={{ marginBottom: 18 }}>
      <div className="patient-details-top">
        <div className="patient-avatar-large" style={{ overflow: "hidden" }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Patient"
              className="patient-avatar-img"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              // Removed onError hiding so you can see if the link is broken (it will show a broken icon)
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>

              <div className="field-value">
                {current?.child?.childId ?? "-"}
              </div>
            </div>
            <div>
              <span className="field-label">Name</span>
              <div className="field-value">{current?.child?.name ?? "-"}</div>
            </div>
          </div>

          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">{displayAge}</div>
            </div>
            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">{displayGender}</div>
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div
          className="patient-action-buttons"
          style={{ marginTop: 16, display: "flex", gap: "12px" }}
        >
          <button
            className="start-btn"
            style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => current && doPost("start")}
            disabled={loading || !current}
          >
            {loading ? "Starting..." : "Start"}
          </button>

          <button
            className="cancel-button"
            style={{
              backgroundColor: "#FF9800", // 🟠 ORANGE
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => doPost("cancel")}
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Cancel"}
          </button>

          <button
            className="end-btn"
            style={{
              backgroundColor: "#f44336",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => {
              if (validateBeforeComplete && !validateBeforeComplete()) return;
              doPost("complete");
            }}
          >
            {loading ? "Completing..." : "Complete"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Nurse1ActivePatientCard;
